import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { initialData } from "@/lib/screeningData";
import { getLicense, createCheckout, saveLicenseConfig, authorizeReport, formatApiErrorDetail } from "@/lib/api";
import { generatePdf } from "@/lib/pdfReport";
import { savePendingReport, loadPendingReport, clearPendingReport } from "@/lib/pendingReport";
import { useAuth } from "@/context/AuthContext";
import { useI18n, stepLabel } from "@/i18n";
import { Intro } from "@/components/steps/Intro";
import { IntakeForm } from "@/components/steps/IntakeForm";
import { Questionnaire } from "@/components/steps/Questionnaire";
import { SnellenTest } from "@/components/steps/SnellenTest";
import { AstigmatismTest } from "@/components/steps/AstigmatismTest";
import { ContrastTest } from "@/components/steps/ContrastTest";
import { NearVisionTest } from "@/components/steps/NearVisionTest";
import { AmslerTest } from "@/components/steps/AmslerTest";
import { IshiharaTest } from "@/components/steps/IshiharaTest";
import { RedGreenTest } from "@/components/steps/RedGreenTest";
import { Results } from "@/components/steps/Results";
import { Configuration } from "@/components/Configuration";
import { Paywall } from "@/components/Paywall";
import { UpgradeDialog } from "@/components/UpgradeDialog";
import { FullScreen } from "@/components/FullScreen";
import {
  Eye, User, ClipboardList, Contrast, ScanEye, Grid3x3, Palette, Blend, Radar, FileText,
  ChevronLeft, ChevronRight, Settings, LogOut, ShieldCheck, Gift, Lock,
} from "lucide-react";

const RESULTS_STEP = 10;

function newReportId() {
  try {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  } catch { /* noop */ }
  return `r-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

// Identità del lavoratore refertato. Un'autorizzazione vale per *questo* referto: se
// cambia la persona, non è più lo stesso documento e il credito va richiesto di nuovo.
// Solo i campi identificativi — correggere una nota o un esito deve restare gratuito (E2).
const identityOf = (p) =>
  [p.cognome, p.nome, p.dataNascita, p.dataEsame].map((v) => (v || "").trim()).join("|");

const STEPS = [
  { key: "intro" },
  { key: "intake", label: "Anagrafica", icon: User },
  { key: "quest", label: "Sintomi", icon: ClipboardList },
  { key: "snellen", label: "Lontano", icon: Eye },
  { key: "astigmatism", label: "Astigmatismo", icon: Radar },
  { key: "contrast", label: "Contrasto", icon: Contrast },
  { key: "near", label: "Vicino", icon: ScanEye },
  { key: "amsler", label: "Amsler", icon: Grid3x3 },
  { key: "ishihara", label: "Colori", icon: Palette },
  { key: "redgreen", label: "Rosso-Verde", icon: Blend },
  { key: "results", label: "Report", icon: FileText },
];

const EMPTY_ENTITLEMENT = { unlocked: false, freeRemaining: null, hasLogo: false, hasFirma: false, configEditable: true };

export default function ScreeningApp() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { t, lang } = useI18n();
  const [step, setStep] = useState(0);
  const [view, setView] = useState("wizard");
  const [data, setData] = useState(initialData);

  // Lo stato `paywall` non esiste più: nessun utente registrato incontra un muro.
  const [gate, setGate] = useState("loading"); // loading | setup | ready
  const [licenseConfig, setLicenseConfig] = useState(null);
  const [entitlement, setEntitlement] = useState(EMPTY_ENTITLEMENT);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [reportId, setReportId] = useState(newReportId);
  // Asset consegnati da /reports/authorize: vivono solo in memoria React per la durata
  // della sessione. Metterli in cache riaprirebbe esattamente il bypass che il server chiude
  // trattenendoli in GET /license.
  const [authorizedAssets, setAuthorizedAssets] = useState(null);
  // Identità per cui `authorizedAssets` è stato concesso: serve a invalidare
  // l'autorizzazione quando cambia il lavoratore.
  const [authorizedIdentity, setAuthorizedIdentity] = useState(null);
  const bootstrapped = useRef(false);
  const autoDownloadRef = useRef(false);

  const applyConfig = useCallback((config) => {
    setLicenseConfig(config);
    setData((d) => ({ ...d, patient: { ...d.patient, ...config } }));
  }, []);

  const loadLicense = useCallback(async () => {
    let lic = null;
    try {
      lic = await getLicense();
    } catch (e) {
      // Fallback permissivo: un errore di rete non deve mai trasformarsi in un muro.
      toast.error(t("toast_license_err"));
    }
    const cfg = lic?.config || null;
    setLicenseConfig(cfg);
    setEntitlement({
      unlocked: !!lic?.unlocked,
      freeRemaining: lic?.free_remaining ?? null,
      hasLogo: !!lic?.has_logo,
      hasFirma: !!lic?.has_firma,
      configEditable: lic?.config_editable !== false,
    });

    // Referto messo in sospeso prima di andare a pagare: chi paga a metà lavoro deve
    // ritrovare il proprio lavoro, non un wizard vuoto.
    const pending = loadPendingReport();
    if (pending) {
      clearPendingReport();
      setReportId(pending.reportId);
      // Il payload viene da sessionStorage e può essere stato scritto da una versione
      // precedente dell'app: una sezione aggiunta dopo il salvataggio arriverebbe qui assente
      // e farebbe esplodere sia lo step che la costruzione del PDF. `initialData()` fa da
      // base — le sezioni presenti nel pending vincono comunque per intero.
      setData({ ...initialData(), ...pending.data, patient: { ...pending.data.patient, ...(cfg || {}) } });
      setStep(RESULTS_STEP);
      if (location.state?.resumeReport && lic?.unlocked) autoDownloadRef.current = true;
    } else {
      if (cfg) applyConfig(cfg);
      // Si torna dal pagamento ma il referto non c'è più (sessionStorage negato, o scaduto):
      // meglio dirlo che presentare un wizard vuoto senza spiegazione.
      if (location.state?.resumeReport) toast.message(t("toast_pending_lost"));
    }

    setGate(lic && !lic.configured ? "setup" : "ready");
    // `resumeReport` viene rimosso dalla history una volta consumato: senza, ogni refresh
    // successivo di /app lo rileggerebbe e ripeterebbe il messaggio all'infinito.
    if (location.state?.resumeReport) navigate(location.pathname, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applyConfig]);

  useEffect(() => {
    if (bootstrapped.current) return;
    bootstrapped.current = true;
    loadLicense();
  }, [loadLicense]);

  // Chi paga in un'altra scheda deve poter tornare qui e trovare il muro sparito: senza,
  // questa scheda continuerebbe a chiedere un importo già pagato, che si legge come un
  // pagamento fallito. Tocca *solo* l'entitlement — non `applyConfig`, che rimescolerebbe
  // licenseConfig dentro data.patient sovrascrivendo il medico digitato nell'anagrafica —
  // e tace in caso di errore: è un aggiornamento di sfondo, non un'azione dell'utente.
  useEffect(() => {
    const refreshEntitlement = async () => {
      if (document.visibilityState !== "visible") return;
      try {
        const lic = await getLicense();
        setEntitlement({
          unlocked: !!lic?.unlocked,
          freeRemaining: lic?.free_remaining ?? null,
          hasLogo: !!lic?.has_logo,
          hasFirma: !!lic?.has_firma,
          configEditable: lic?.config_editable !== false,
        });
      } catch { /* aggiornamento opportunistico: si ignora */ }
    };
    window.addEventListener("focus", refreshEntitlement);
    document.addEventListener("visibilitychange", refreshEntitlement);
    return () => {
      window.removeEventListener("focus", refreshEntitlement);
      document.removeEventListener("visibilitychange", refreshEntitlement);
    };
  }, []);

  const currentIdentity = identityOf(data.patient);

  // Cambiare lavoratore dopo un referto autorizzato non deve produrne un secondo gratis.
  // Senza questo basterebbe tornare indietro dal riepilogo, sostituire l'anagrafica e
  // premere "Scarica di nuovo": il server, interrogato con lo stesso report_id, risponderebbe
  // `replay` e riconsegnerebbe gli asset — un referto pulito e valido per un'altra persona.
  useEffect(() => {
    if (!authorizedIdentity || authorizedIdentity === currentIdentity) return;
    setAuthorizedAssets(null);
    setAuthorizedIdentity(null);
    setReportId(newReportId());
    // Gli asset vanno tolti anche dai dati: il documento torna a essere un'anteprima.
    setData((d) => ({
      ...d,
      patient: { ...d.patient, logo: licenseConfig?.logo ?? null, firma: licenseConfig?.firma ?? null },
    }));
  }, [currentIdentity, authorizedIdentity, licenseConfig]);

  const update = (section, patch) =>
    setData((d) => ({ ...d, [section]: { ...d[section], ...patch } }));

  const restart = () => {
    const base = initialData();
    if (licenseConfig) base.patient = { ...base.patient, ...licenseConfig };
    setData(base);
    setStep(0);
    setView("wizard");
    // Nuova compilazione = nuovo referto: l'idempotenza lato server è per report_id.
    setReportId(newReportId());
    setAuthorizedAssets(null);
    setAuthorizedIdentity(null);
    // Un referto in sospeso appartiene alla compilazione appena abbandonata: lasciarlo
    // lo farebbe riemergere al primo rimontaggio, sovrascrivendo il lavoratore corrente
    // con i dati clinici del precedente.
    clearPendingReport();
  };

  const handleUnlock = async () => {
    setCheckoutLoading(true);
    try {
      const { checkout_url } = await createCheckout();
      window.location.href = checkout_url;
    } catch (e) {
      toast.error(t("toast_checkout_err"));
      setCheckoutLoading(false);
    }
  };

  const handleSaveConfig = async (config) => {
    try {
      const res = await saveLicenseConfig(config);
      applyConfig(res.config);
      setEntitlement((e) => ({
        ...e,
        hasLogo: !!res.config?.logo,
        hasFirma: !!res.config?.firma,
        configEditable: res.config_editable !== false,
      }));
      toast.success(t("toast_config_saved"));
      setGate("ready");
      setView("wizard");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(detail ? formatApiErrorDetail(detail) : t("toast_config_err"));
    }
  };

  const handleGenerate = useCallback(async () => {
    setGenerating(true);
    try {
      const res = await authorizeReport(reportId, lang);
      // Il PDF pulito si compone SOLO con gli asset appena ricevuti dal server.
      const assets = res.assets || {};
      const merged = { ...data, patient: { ...data.patient, ...assets } };
      setAuthorizedAssets(assets);
      setAuthorizedIdentity(identityOf(merged.patient));
      setData(merged);
      generatePdf(merged, lang, { clean: true });
      if (res.mode === "free") {
        setEntitlement((e) => ({ ...e, freeRemaining: res.free_remaining, configEditable: false }));
        toast.success(t("toast_free_report_used"));
      } else if (res.mode === "replay") {
        setEntitlement((e) => ({ ...e, freeRemaining: res.free_remaining }));
      }
    } catch (e) {
      const status = e?.response?.status;
      if (status === 402) {
        // I dati non vanno persi: si mettono in sospeso e si apre il dialog, non un redirect.
        savePendingReport({ reportId, data, step });
        setUpgradeOpen(true);
      } else if (status === 409) {
        toast.error(t("toast_report_setup"));
        setGate("setup");
      } else {
        toast.error(t("toast_report_err"));
      }
    } finally {
      setGenerating(false);
    }
  }, [reportId, lang, data, step, t]);

  // Ripresa dopo il pagamento: il download parte da solo, una volta sola. È `autoDownloadRef`
  // a garantire l'unicità, quindi l'effetto può dipendere onestamente da `handleGenerate`
  // invece di raggiungerlo attraverso una ref assegnata in fase di render.
  useEffect(() => {
    if (gate !== "ready" || !autoDownloadRef.current) return;
    autoDownloadRef.current = false;
    handleGenerate();
  }, [gate, handleGenerate]);

  const handleUpgradeOpenChange = (open) => {
    setUpgradeOpen(open);
    // "Non adesso": i dati restano a schermo, ma il referto in sospeso va rimosso. Tenerlo
    // significherebbe vederselo ricomparire al prossimo rimontaggio della pagina, magari
    // sopra un altro lavoratore — e restare su sessionStorage senza motivo.
    if (!open) clearPendingReport();
  };

  const handleUpgradePay = async () => {
    // Il pending è già stato scritto alla ricezione del 402; qui si riscrive perché al
    // dialog si arriva anche dal chip in header, senza essere passati da un 402.
    if (!savePendingReport({ reportId, data, step })) toast.message(t("toast_pending_lost"));
    await handleUnlock();
  };

  const doLogout = async () => { clearPendingReport(); navigate("/"); await logout(); };

  const canProceed = () => {
    if (step === 1) {
      const p = data.patient;
      return p.cognome.trim() && p.nome.trim() && p.medico.trim();
    }
    return true;
  };

  // Credito esaurito: si blocca l'*avvio* di un nuovo screening, non il download. Lo
  // screening è il prodotto — chi lo esegue può trascriverne gli esiti a mano, quindi
  // fermarlo al PDF arriva troppo tardi.
  //
  // È un gate di interfaccia, NON un'enforcement: chi blocca GET /api/license ottiene
  // `lic = null` e passa. Il controllo vero resta il 402 di POST /reports/authorize, che
  // è anche l'unico punto da cui escono logo e firma. Non irrigidire questa riga
  // trattandola come una barriera di sicurezza.
  //
  // `freeRemaining` è null sia per gli utenti sbloccati sia quando la chiamata è fallita, e
  // il server non restituisce mai un valore negativo: `=== 0` conserva quindi la scelta di
  // non trasformare un errore di rete in un muro (vedi loadLicense).
  const freeRemaining = entitlement.freeRemaining;
  const blocked = !entitlement.unlocked && freeRemaining === 0;

  if (gate === "loading") return <FullScreen text={t("loading")} />;

  // Prima del gate "setup": a chi non potrà comunque produrre un documento (limite a 0) non
  // si chiede di depositare una volta per sempre intestazione e firma autografa.
  if (blocked && step === 0 && view === "wizard")
    return <Paywall onUnlock={handleUnlock} loading={checkoutLoading} onLogout={doLogout} email={user?.email} />;

  if (gate === "setup")
    return (
      <div className="App min-h-screen bg-slate-50 font-sans text-slate-900">
        <header className="border-b border-slate-200 bg-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <span className="w-9 h-9 rounded-md bg-primary text-white flex items-center justify-center">
                <Eye className="w-5 h-5" strokeWidth={2} />
              </span>
              <span className="leading-tight">
                <span className="block text-base font-bold tracking-tight text-slate-900">VisioScreening</span>
                <span className="block text-[11px] font-medium uppercase tracking-widest text-slate-400">{t("cfg_setup_title")}</span>
              </span>
            </div>
            <Button variant="ghost" onClick={doLogout} data-testid="setup-logout-btn"><LogOut className="w-4 h-4" /> {t("logout")}</Button>
          </div>
        </header>
        <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-10">
          <Configuration mode="setup" licenseConfig={licenseConfig} entitlement={entitlement}
            data={data} update={update} onSaveConfig={handleSaveConfig} />
        </main>
      </div>
    );

  // ----- Ready: full wizard -----
  const stepProps = { data, update };
  const content = [
    <Intro key="intro" onStart={() => setStep(1)} />,
    <IntakeForm key="intake" {...stepProps} />,
    <Questionnaire key="quest" {...stepProps} />,
    <SnellenTest key="snellen" {...stepProps} />,
    <AstigmatismTest key="astigmatism" {...stepProps} />,
    <ContrastTest key="contrast" {...stepProps} />,
    <NearVisionTest key="near" {...stepProps} />,
    <AmslerTest key="amsler" {...stepProps} />,
    <IshiharaTest key="ishihara" {...stepProps} />,
    <RedGreenTest key="redgreen" {...stepProps} />,
    <Results key="results" {...stepProps} onRestart={restart} onBack={() => setStep((s) => Math.max(0, s - 1))}
      entitlement={entitlement} authorized={!!authorizedAssets} generating={generating} onGenerate={handleGenerate} />,
  ][step];

  const showFooter = view === "wizard" && step >= 1 && step <= 9;
  const isResults = step === RESULTS_STEP;
  const isConfig = view === "config";
  const showFreeChip = !entitlement.unlocked && freeRemaining > 0;
  // Stessa condizione del muro: derivarle entrambe da `blocked` evita lo stato ibrido
  // (muro senza chip, o chip senza muro).
  const showUpgradeChip = blocked;

  return (
    <div className="App min-h-screen bg-slate-50 font-sans text-slate-900">
      <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/90 backdrop-blur">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16">
            {/* Il logo è navigazione, non un comando: con il credito esaurito `restart` cancellerebbe
                il referto appena prodotto e — generando un nuovo reportId — brucerebbe anche il replay
                che ne consentiva il ri-download, per depositare l'utente su un muro. */}
            <button onClick={blocked ? () => setUpgradeOpen(true) : restart} data-testid="brand-home" className="flex items-center gap-2.5">
              <span className="w-9 h-9 rounded-md bg-primary text-white flex items-center justify-center">
                <Eye className="w-5 h-5" strokeWidth={2} />
              </span>
              <span className="text-left leading-tight">
                <span className="block text-base font-bold tracking-tight text-slate-900">VisioScreening</span>
                <span className="block text-[11px] font-medium uppercase tracking-widest text-slate-400">{t("brand_sub")}</span>
              </span>
            </button>
            <div className="flex items-center gap-2">
              {/* Nessun bottone di acquisto finché il credito è disponibile: non si vende
                  prima di aver consegnato. */}
              {showFreeChip && (
                <span data-testid="free-credit-chip"
                  className="hidden sm:inline-flex items-center gap-1.5 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700">
                  <Gift className="w-4 h-4" />
                  {freeRemaining === 1 ? t("free_credit_chip") : t("free_credit_chip_n").replace("{n}", freeRemaining)}
                </span>
              )}
              {showUpgradeChip && (
                <button onClick={() => setUpgradeOpen(true)} data-testid="upgrade-chip"
                  className="hidden sm:inline-flex items-center gap-1.5 rounded-md border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-700 hover:bg-amber-100 transition-colors">
                  <Lock className="w-4 h-4" /> {t("free_credit_used_chip")}
                </button>
              )}
              {user?.role === "admin" && (
                <button onClick={() => navigate("/admin")} data-testid="nav-admin-button"
                  className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 transition-colors">
                  <ShieldCheck className="w-4 h-4" /> {t("admin")}
                </button>
              )}
              <button onClick={() => setView((v) => (v === "config" ? "wizard" : "config"))} data-testid="config-toggle-button"
                className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-semibold transition-colors ${
                  isConfig ? "border-primary bg-primary text-white" : "border-slate-200 text-slate-600 hover:bg-slate-50"
                }`}>
                <Settings className="w-4 h-4" /> {t("config")}
              </button>
              <button onClick={doLogout} data-testid="app-logout-button"
                className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 transition-colors">
                <LogOut className="w-4 h-4" /> {t("logout")}
              </button>
            </div>
          </div>

          {step >= 1 && !isConfig && (
            <nav className="flex items-center gap-1 overflow-x-auto pb-3 -mb-px" data-testid="stepper">
              {STEPS.slice(1).map((s, i) => {
                const stepIndex = i + 1;
                const active = stepIndex === step;
                const done = stepIndex < step;
                return (
                  <div key={s.key} className="flex items-center flex-shrink-0">
                    <div className={`flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-semibold transition-colors ${
                      active ? "bg-primary text-white" : done ? "text-primary" : "text-slate-400"
                    }`}>
                      <s.icon className="w-4 h-4" strokeWidth={1.8} />
                      <span className="hidden sm:inline whitespace-nowrap">{stepLabel(lang, stepIndex)}</span>
                    </div>
                    {i < STEPS.length - 2 && <ChevronRight className="w-3.5 h-3.5 text-slate-300 mx-0.5" />}
                  </div>
                );
              })}
            </nav>
          )}
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-10 pb-32">
        {isConfig
          ? <Configuration mode="locked" licenseConfig={licenseConfig} entitlement={entitlement}
              data={data} update={update} onSaveConfig={handleSaveConfig} onClose={() => setView("wizard")} />
          : content}
      </main>

      <UpgradeDialog open={upgradeOpen} onOpenChange={handleUpgradeOpenChange} onPay={handleUpgradePay}
        loading={checkoutLoading} patientName={`${data.patient.cognome} ${data.patient.nome}`.trim()} />

      {showFooter && (
        <footer className="fixed bottom-0 inset-x-0 z-30 border-t border-slate-200 bg-white/95 backdrop-blur">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
            {/* Con il credito esaurito lo step 0 è il muro: si ferma il "Indietro" allo step 1,
                altrimenti l'utente cadrebbe sul muro con la compilazione ancora in memoria ma
                irraggiungibile. */}
            <Button variant="outline" data-testid="back-button" onClick={() => setStep((s) => Math.max(blocked ? 1 : 0, s - 1))}>
              <ChevronLeft className="w-4 h-4" /> {t("back")}
            </Button>
            {!isResults && (
              <Button data-testid="next-button" disabled={!canProceed()} onClick={() => setStep((s) => s + 1)}
                className="bg-primary hover:bg-primary/90 transition-colors px-6">
                {t("next")} <ChevronRight className="w-4 h-4" />
              </Button>
            )}
            {isResults && (
              <span className="text-xs font-medium text-slate-400 pr-1" />
            )}
          </div>
        </footer>
      )}
    </div>
  );
}
